const MAX_SAMPLE_MS = 2000;
const MAX_PENDING = 8;

/** Connection-local nonce correlation; wall-clock changes cannot alter RTT. */
export class RttTracker {
  private nextNonce = 1;
  private pending = new Map<number, number>();
  private estimate?: number;
  private latestSample?: number;
  /** Current connection measurement for deadlines; the display EWMA can lag
   * behind a lasting latency increase and must not schedule recovery probes. */
  get sampleMs(): number | undefined {
    return this.latestSample;
  }
  request(now: number): number {
    for (const [nonce, sent] of this.pending)
      if (now - sent > MAX_SAMPLE_MS) this.pending.delete(nonce);
    while (this.pending.size >= MAX_PENDING)
      this.pending.delete(this.pending.keys().next().value!);
    const nonce = this.nextNonce;
    this.nextNonce = nonce === Number.MAX_SAFE_INTEGER ? 1 : nonce + 1;
    this.pending.set(nonce, now);
    return nonce;
  }
  cancel(nonce: number): void {
    this.pending.delete(nonce);
  }
  receive(nonce: unknown, now: number): number | undefined {
    if (typeof nonce !== "number" || !Number.isSafeInteger(nonce)) return;
    const sent = this.pending.get(nonce);
    if (sent === undefined) return;
    this.pending.delete(nonce);
    const sample = now - sent;
    if (!Number.isFinite(sample) || sample < 0 || sample > MAX_SAMPLE_MS)
      return;
    this.latestSample = sample;
    this.estimate =
      this.estimate === undefined
        ? sample
        : this.estimate + (sample - this.estimate) / 8;
    return this.estimate;
  }
  reset(): void {
    this.pending.clear();
    this.estimate = undefined;
    this.latestSample = undefined;
    // Keep nonces unique across reconnections; delayed replies cannot match a new ping.
  }
}
